import { fg } from '@atlaskit/platform-feature-flags';

import { type ExperimentExperience } from '../../../schemas/dynamic-config';
import type {
	BackendExperiment,
	CombinedLayerExperiment,
	NonOverlappingBackendExperiment,
} from '../../../types';
import {
	STATSIG_SEARCH_BACKEND_L1_LAYER_NAME,
	STATSIG_SEARCH_BACKEND_L2_LAYER_NAME,
	STATSIG_SEARCH_BACKEND_L3_LAYER_NAME,
	STATSIG_SEARCH_BACKEND_QUERY_EXPANSION_LAYER_NAME,
} from '../../constants';
import { getLayerValues } from '../backend';

import type {
	DynamicExperiment,
	DynamicExperiments,
	EntityFilters,
	ProductDataRegions,
	ProductEdition,
	SearchExperimentLayer,
} from './types';

const WILDCARD = '*';
const JIRA_ENTITY = 'jira';

export const mapCombinedLayerExperiment = ({
	combinedExperiment,
}: {
	combinedExperiment: CombinedLayerExperiment;
}): BackendExperiment => {
	if (combinedExperiment) {
		const experimentLayers = getExperimentLayer({
			l1Id: combinedExperiment.l1Id,
			l2Id: combinedExperiment.l2Id,
			l3Id: combinedExperiment.l3Id,
			qrId: combinedExperiment.qrId,
			abTestId: combinedExperiment.abTestId,
			controlId: combinedExperiment.controlId,
		});
		return {
			experimentLayers,
		};
	}
	return {
		experimentLayers: [],
	};
};

export const mapNonOverlappingBackendExperiment = ({
	nonOverlappingExperiment,
}: {
	nonOverlappingExperiment: NonOverlappingBackendExperiment;
}): BackendExperiment => {
	if (nonOverlappingExperiment) {
		const experimentLayers = getExperimentLayer({
			l1Id: nonOverlappingExperiment.l1Id,
			l2Id: nonOverlappingExperiment.l2Id,
			l3Id: nonOverlappingExperiment.l3Id,
			qrId: nonOverlappingExperiment.qrId,
			abTestId: nonOverlappingExperiment.abTestId,
			controlId: nonOverlappingExperiment.controlId,
		});
		return {
			experimentLayers,
		};
	}
	return {
		experimentLayers: [],
	};
};

export const mapDynamicExperiments = ({
	dynamicExperiments,
	requestedEntities,
	productEdition,
	isHello,
	productDataRegions,
	experience,
	tenantId,
	isJiraIssueIndexed,
}: {
	dynamicExperiments: DynamicExperiments;
	requestedEntities: EntityFilters;
	productEdition?: ProductEdition;
	isHello?: boolean;
	isJiraIssueIndexed?: boolean;
	productDataRegions?: ProductDataRegions;
	experience?: ExperimentExperience;
	tenantId?: string;
}): BackendExperiment => {
	const experiments = dynamicExperiments
		.filter(containsTenantId(tenantId))
		.filter(isSameConfluenceTier(productEdition))
		.filter(containsExperience(experience))
		.filter(isHelloTenant(isHello))
		.filter(containsRequestedEntities(requestedEntities))
		.filter(containsRequestedSubEntities(requestedEntities))
		.filter(isJiraIssueIndexedConditionSatisfied(isJiraIssueIndexed))
		.filter(passesTargetingGate())
		.filter(satisfiesProductDataRegions(productDataRegions))
		.reduce<DynamicExperiment[]>((layerFunctions, currentLayerFunction) => {
			// IF there are more than one definitions with the same layerType, hello, entity and subEntity
			// THEN only add the first one defined, (do not add this one)
			const containsCollision = layerFunctions.find(
				(layerFunction) =>
					layerFunction.layerType === currentLayerFunction.layerType &&
					(layerFunction.entity === currentLayerFunction.entity ||
						currentLayerFunction.entity === WILDCARD ||
						layerFunction.entity === WILDCARD) &&
					(layerFunction.subEntity === currentLayerFunction.subEntity ||
						currentLayerFunction.subEntity === WILDCARD ||
						layerFunction.subEntity === WILDCARD),
			);
			return containsCollision ? layerFunctions : [...layerFunctions, currentLayerFunction];
		}, [])
		.map((dynamicExp) => {
			const { layerId, shadowId, abTestId, controlId } = getLayerValues(dynamicExp.statsigLayerId);
			return {
				definition: {
					layerId,
					shadowId,
					entity: dynamicExp.entity,
					subEntity: dynamicExp.subEntity,
					abTestId,
					controlId,
					statsigLayerId: dynamicExp.statsigLayerId,
				},
				layerType: dynamicExp.layerType,
			};
		});

	const groupedExperimentLayers = experiments.reduce<SearchExperimentLayer[]>(
		(experimentLayers, definitionWithLayerType) => {
			const existingLayer = experimentLayers.find(
				(layer) => layer.name === definitionWithLayerType.layerType,
			);
			const { controlId, statsigLayerId, ...newDefinition } = definitionWithLayerType.definition;
			const analyticsDefinition = {
				...definitionWithLayerType.definition,
			};

			if (!existingLayer) {
				experimentLayers.push({
					name: definitionWithLayerType.layerType,
					layerId: null, // TODO QS-6283: Remove this once scalable statsig layers are rolled out
					shadowId: null, // TODO QS-6283: Remove this once scalable statsig layers are rolled out
					definitions: [newDefinition],
					analyticsDefinitions: [analyticsDefinition],
				});
				return experimentLayers;
			}

			existingLayer.definitions?.push(newDefinition);
			existingLayer.analyticsDefinitions?.push(analyticsDefinition);

			return experimentLayers;
		},
		[],
	);

	return {
		experimentLayers: groupedExperimentLayers,
	};
};

export const isSameConfluenceTier =
	(productEdition?: ProductEdition) =>
	(dynamicExp: DynamicExperiment): boolean => {
		// Opt for specificity. Does not filter out values that do not have the confluenceTier attribute
		if (!dynamicExp.confluenceTier || dynamicExp.confluenceTier.length === 0) {
			return true;
		}
		return !!dynamicExp.confluenceTier?.find(
			(experimentTier) => productEdition?.['confluence'] === experimentTier,
		);
	};

const isHelloTenant = (isHello?: boolean) => (dynamicExp: DynamicExperiment) => {
	if (dynamicExp.hello === undefined || dynamicExp.hello === null) {
		return true;
	}

	return dynamicExp.hello === isHello;
};

const passesTargetingGate = () => (dynamicExp: DynamicExperiment) => {
	if (dynamicExp.targetingGateId === undefined || dynamicExp.targetingGateId === null) {
		return true;
	}

	// eslint-disable-next-line @atlaskit/platform/static-feature-flags
	return fg(dynamicExp.targetingGateId);
};

/**
 * This is specifically used for targeting based on indexing status of the tenant.
 * This might NOT be needed once Jira indexed and ROVO in Jira is shipped to 100%
 */
const isJiraIssueIndexedConditionSatisfied =
	(isJiraIssueIndexed?: boolean) => (dynamicExp: DynamicExperiment) => {
		if (
			isJiraIssueIndexed === undefined ||
			dynamicExp.isJiraIssueIndexed === undefined ||
			dynamicExp.isJiraIssueIndexed === null ||
			dynamicExp?.entity !== JIRA_ENTITY
		) {
			return true;
		}

		return dynamicExp.isJiraIssueIndexed === isJiraIssueIndexed;
	};

const containsRequestedEntities =
	(entityFilter: EntityFilters) => (dynamicExp: DynamicExperiment) => {
		if (!dynamicExp.entity || dynamicExp.entity === WILDCARD) {
			return true;
		}

		const isFilteredEntity = entityFilter.find(
			(filteredEntity) => dynamicExp.entity === filteredEntity.entity,
		);
		return isFilteredEntity ?? false;
	};

const containsRequestedSubEntities =
	(entityFilter: EntityFilters) => (dynamicExp: DynamicExperiment) => {
		if (!dynamicExp.subEntity || dynamicExp.subEntity === WILDCARD) {
			return true;
		}

		const isFilteredSubEntity = entityFilter.find(
			(filteredEntity) => dynamicExp.subEntity === filteredEntity.subEntity,
		);
		return isFilteredSubEntity ?? false;
	};

/**
 * Determines whether the provided data regions (dataRegions) are defined in
 * the dynamic configuration (dynamicExp). Every data region for a given product
 * in the dynamic configuration must be specified  in order for the given
 * experiment layer to be eligible.
 *
 * @param dataRegions Represents where the data comes from comes from for
 * each product.
 * ```
 * { 'confluence': ["us-west-2"], 'compass': ["us-east-1"] }`
 * ```
 *
 * @returns a boolean that indicates whether or not the provided data regions
 * (dataRegions) are specified in the dynamic configuration (dynamicExp).
 */
const satisfiesProductDataRegions =
	(dataRegions?: ProductDataRegions) => (dynamicExp: DynamicExperiment) => {
		if (!dynamicExp.productDataRegions) {
			return true;
		}

		return Object.keys(dynamicExp.productDataRegions)
			.map((product) => {
				const productDataRegions = dynamicExp.productDataRegions?.[product];
				const isProductEligible = dataRegions?.[product]?.every((dataRegion) => {
					return productDataRegions?.includes(dataRegion);
				});
				return !!isProductEligible;
			})
			.reduce((prev, curr) => prev && curr);
	};

const containsExperience =
	(experience?: ExperimentExperience) => (dynamicExp: DynamicExperiment) => {
		if (!dynamicExp.experiences || dynamicExp.experiences.length === 0) {
			return true;
		}

		return dynamicExp.experiences?.find(
			(dynamicExpExperience) => experience === dynamicExpExperience,
		);
	};

const containsTenantId = (tenantId?: string) => (dynamicExp: DynamicExperiment) => {
	if (!dynamicExp.tenantIds || dynamicExp.tenantIds.length === 0) {
		return true;
	}

	return dynamicExp.tenantIds?.find((dynamicExpTenantId) => tenantId === dynamicExpTenantId);
};

const getExperimentLayer = ({
	l1Id,
	l2Id,
	l3Id,
	qrId,
	abTestId,
	controlId,
}: {
	l1Id: string | null;
	l2Id: string | null;
	l3Id: string | null;
	qrId: string | null;
	abTestId: string | null;
	controlId: string | null;
}) => {
	const experimentLayers = [
		[STATSIG_SEARCH_BACKEND_L1_LAYER_NAME, l1Id],
		[STATSIG_SEARCH_BACKEND_L2_LAYER_NAME, l2Id],
		[STATSIG_SEARCH_BACKEND_L3_LAYER_NAME, l3Id],
		[STATSIG_SEARCH_BACKEND_QUERY_EXPANSION_LAYER_NAME, qrId],
	];
	return experimentLayers.map(([layerType, layerId]) => {
		return {
			name: layerType,
			layerId: null,
			shadowId: null,
			definitions: [
				{
					layerId,
					shadowId: null,
					entity: WILDCARD,
					subEntity: WILDCARD,
					abTestId: abTestId ?? null,
				},
			],
			analyticsDefinitions: [
				{
					layerId,
					shadowId: null,
					entity: WILDCARD,
					subEntity: WILDCARD,
					abTestId: abTestId ?? null,
					controlId: controlId ?? null,
				},
			],
		};
	});
};
